import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { useLocation } from '../../../../hooks/useLocation';
import { useSearchParams } from 'react-router-dom';
import { ValidDataService } from '../../../../service/validDataService';
import { ProcurementService } from '../../../../service/procurementService';
import NewOrdersTab from './NewOrdersTab';
import PlaceOrderTab from './PlaceOrderTab';
import ReceiveStockTab from './ReceiveStockTab';
import ReceiptHistoryTab from './ReceiptHistoryTab';

const buildPoLine = () => ({
  inventoryItemId: '',
  inventoryItem: null,
  orderedQty: 1,
  unitCost: '',
  landingCost: '',
  notes: ''
});

const buildDirectReceiveLine = () => ({
  inventoryItemId: '',
  inventoryItem: null,
  catalogProductId: null,
  receivedQty: 1,
  acceptedQty: 1,
  damagedQty: 0,
  unitCostAtReceipt: '',
  landingCostAtReceipt: 0,
  notes: ''
});

export default function Procurement() {
  const queryClient = useQueryClient();
  const { selectedLocation } = useLocation();
  const [searchParams] = useSearchParams();
  const appliedPrefillKeyRef = useRef('');
  const [tab, setTab] = useState(0);
  const [receiveMode, setReceiveMode] = useState('po');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [receiptDetailOpen, setReceiptDetailOpen] = useState(false);
  const [receiptFilters, setReceiptFilters] = useState({
    invoiceNumber: '',
    supplierId: '',
    locationId: selectedLocation?.id || '',
    fromDate: '',
    toDate: ''
  });
  const [receiptPagination, setReceiptPagination] = useState({
    page: 0,
    rowsPerPage: 25
  });
  const [newOrdersSearch, setNewOrdersSearch] = useState('');

  const [poForm, setPoForm] = useState({
    supplierId: '',
    locationId: selectedLocation?.id || '',
    expectedDate: '',
    notes: '',
    lines: [buildPoLine()]
  });

  const [receiveForm, setReceiveForm] = useState({
    purchaseOrderId: '',
    supplierId: '',
    locationId: selectedLocation?.id || '',
    supplierInvoiceNumber: '',
    supplierInvoiceDate: '',
    consignmentReference: '',
    consignmentType: 'none',
    notes: '',
    lines: []
  });

  const { data: suppliersResp } = useQuery(['procurement-suppliers'], () =>
    ValidDataService.getAllSuppliers('frames')
  );

  const newOrdersQueryParams = useMemo(() => {
    return {
      page: 1,
      pageSize: 100,
      locationId: selectedLocation?.id || undefined,
      search: newOrdersSearch.trim() || undefined
    };
  }, [newOrdersSearch, selectedLocation?.id]);

  const {
    data: newOrdersResp,
    isLoading: isNewOrdersLoading
  } = useQuery(
    ['procurement-new-orders', newOrdersQueryParams],
    () => ProcurementService.listNewOrders(newOrdersQueryParams),
    { refetchOnWindowFocus: false }
  );

  const purchaseOrderQueryParams = useMemo(() => {
    return {
      page: 1,
      pageSize: 100,
      locationId: receiveForm.locationId || undefined,
      supplierId: receiveMode === 'po' ? (receiveForm.supplierId || undefined) : undefined
    };
  }, [receiveForm.locationId, receiveForm.supplierId, receiveMode]);

  const { data: purchaseOrdersResp } = useQuery(
    ['procurement-purchase-orders', purchaseOrderQueryParams],
    () => ProcurementService.listPurchaseOrders(purchaseOrderQueryParams),
    { refetchOnWindowFocus: false }
  );

  const receiptQueryParams = useMemo(() => {
    return {
      page: receiptPagination.page + 1,
      pageSize: receiptPagination.rowsPerPage,
      invoiceNumber: receiptFilters.invoiceNumber.trim() || undefined,
      supplierId: receiptFilters.supplierId || undefined,
      locationId: receiptFilters.locationId || undefined,
      fromDate: receiptFilters.fromDate || undefined,
      toDate: receiptFilters.toDate || undefined
    };
  }, [receiptFilters, receiptPagination.page, receiptPagination.rowsPerPage]);

  const {
    data: receiptsResp,
    isLoading: isReceiptsLoading,
    isFetching: isReceiptsFetching
  } = useQuery(
    ['procurement-receipts', receiptQueryParams],
    () => ProcurementService.listReceipts(receiptQueryParams),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true
    }
  );

  const suppliers = useMemo(() => suppliersResp?.data || [], [suppliersResp?.data]);
  const newOrders = useMemo(() => newOrdersResp?.data?.data || [], [newOrdersResp?.data?.data]);
  const purchaseOrders = useMemo(() => purchaseOrdersResp?.data?.data || [], [purchaseOrdersResp?.data?.data]);
  const receipts = useMemo(() => receiptsResp?.data?.data || [], [receiptsResp?.data?.data]);
  const receiptCount = Number(receiptsResp?.data?.count || 0);

  const purchaseOrderById = useMemo(() => {
    const map = new Map();
    purchaseOrders.forEach(po => map.set(po.id, po));
    return map;
  }, [purchaseOrders]);

  useEffect(() => {
    if (!selectedLocation?.id) {
      return;
    }

    setPoForm(prev => ({ ...prev, locationId: selectedLocation.id }));
    setReceiveForm(prev => ({ ...prev, locationId: selectedLocation.id }));
    setReceiptFilters(prev => ({ ...prev, locationId: selectedLocation.id }));
  }, [selectedLocation?.id]);

  useEffect(() => {
    const procTabParam = searchParams.get('procTab');
    if (procTabParam === null) {
      return;
    }

    const parsedTab = Number(procTabParam);
    if (!Number.isNaN(parsedTab) && parsedTab >= 0 && parsedTab <= 3) {
      setTab(parsedTab);
    }
  }, [searchParams]);

  useEffect(() => {
    const prefillInventoryItemId = Number(searchParams.get('prefillInventoryItemId') || 0) || null;
    if (!prefillInventoryItemId) {
      return;
    }

    const prefillKey = searchParams.toString();
    if (appliedPrefillKeyRef.current === prefillKey) {
      return;
    }

    const prefillSupplierId = searchParams.get('prefillSupplierId') || '';
    const prefillLocationId = searchParams.get('prefillLocationId') || '';
    const prefillModelNumber = searchParams.get('prefillModelNumber') || '';
    const prefillUnitCost = searchParams.get('prefillUnitCost') || '';
    const prefillLandingCost = searchParams.get('prefillLandingCost') || '';

    setPoForm(prev => ({
      ...prev,
      supplierId: prefillSupplierId || prev.supplierId,
      locationId: prefillLocationId || prev.locationId,
      lines: [
        {
          ...buildPoLine(),
          inventoryItemId: prefillInventoryItemId,
          inventoryItem: {
            id: prefillInventoryItemId,
            modelNumber: prefillModelNumber
          },
          orderedQty: 1,
          unitCost: prefillUnitCost,
          landingCost: prefillLandingCost
        }
      ]
    }));

    setTab(1);
    appliedPrefillKeyRef.current = prefillKey;
  }, [searchParams]);

  useEffect(() => {
    if (receiveMode !== 'po' || !receiveForm.purchaseOrderId) {
      return;
    }

    const selectedPurchaseOrderStillAvailable = purchaseOrders.some(
      po => Number(po.id) === Number(receiveForm.purchaseOrderId)
    );

    if (!selectedPurchaseOrderStillAvailable) {
      setReceiveForm(prev => ({
        ...prev,
        purchaseOrderId: '',
        lines: []
      }));
    }
  }, [purchaseOrders, receiveForm.purchaseOrderId, receiveMode]);

  const createPoMutation = useMutation(
    (payload) => ProcurementService.createPurchaseOrder(payload),
    {
      onSuccess: () => {
        toast.success('Purchase order created');
        setPoForm({
          supplierId: '',
          locationId: selectedLocation?.id || '',
          expectedDate: '',
          notes: '',
          lines: [buildPoLine()]
        });
        queryClient.invalidateQueries(['procurement-purchase-orders']);
        queryClient.invalidateQueries(['procurement-new-orders']);
      },
      onError: (error) => {
        const message = error?.response?.data?.message || 'Failed to create purchase order';
        toast.error(message);
      }
    }
  );

  const receivePoMutation = useMutation(
    ({ purchaseOrderId, payload }) => ProcurementService.receivePurchaseOrder(purchaseOrderId, payload),
    {
      onSuccess: () => {
        toast.success('Stock received successfully');
        setReceiveForm({
          purchaseOrderId: '',
          supplierId: '',
          locationId: selectedLocation?.id || '',
          supplierInvoiceNumber: '',
          supplierInvoiceDate: '',
          consignmentReference: '',
          consignmentType: 'none',
          notes: '',
          lines: []
        });
        queryClient.invalidateQueries(['procurement-purchase-orders']);
        queryClient.invalidateQueries(['procurement-receipts']);
      },
      onError: (error) => {
        const message = error?.response?.data?.message || 'Failed to receive stock';
        toast.error(message);
      }
    }
  );

  const directReceiveMutation = useMutation(
    (payload) => ProcurementService.createDirectReceipt(payload),
    {
      onSuccess: () => {
        toast.success('Direct stock receipt created');
        setReceiveForm({
          purchaseOrderId: '',
          supplierId: '',
          locationId: selectedLocation?.id || '',
          supplierInvoiceNumber: '',
          supplierInvoiceDate: '',
          consignmentReference: '',
          consignmentType: 'none',
          notes: '',
          lines: [buildDirectReceiveLine()]
        });
        queryClient.invalidateQueries(['procurement-receipts']);
      },
      onError: (error) => {
        const message = error?.response?.data?.message || 'Failed to create direct receipt';
        toast.error(message);
      }
    }
  );

  const cancelPoMutation = useMutation(
    ({ purchaseOrderId, payload }) => ProcurementService.cancelPurchaseOrder(purchaseOrderId, payload),
    {
      onSuccess: () => {
        toast.success('Purchase order cancelled');
        setReceiveForm(prev => ({
          ...prev,
          purchaseOrderId: '',
          lines: []
        }));
        queryClient.invalidateQueries(['procurement-purchase-orders']);
      },
      onError: (error) => {
        const message = error?.response?.data?.message || 'Failed to cancel purchase order';
        toast.error(message);
      }
    }
  );

  const placeQueuedOrderMutation = useMutation(
    ({ pendingOrderId, payload }) => ProcurementService.placeQueuedOrder(pendingOrderId, payload),
    {
      onSuccess: () => {
        toast.success('Queued frame order placed');
        queryClient.invalidateQueries(['procurement-new-orders']);
        queryClient.invalidateQueries(['procurement-purchase-orders']);
      },
      onError: (error) => {
        const message = error?.response?.data?.message || 'Failed to place queued frame order';
        toast.error(message);
      }
    }
  );

  const updateQueuedOrderMutation = useMutation(
    ({ pendingOrderId, payload }) => ProcurementService.updateQueuedOrder(pendingOrderId, payload),
    {
      onSuccess: () => {
        toast.success('Queued frame order updated');
        queryClient.invalidateQueries(['procurement-new-orders']);
      },
      onError: (error) => {
        const message = error?.response?.data?.message || 'Failed to update queued frame order';
        toast.error(message);
      }
    }
  );

  const onPoFieldChange = (field, value) => {
    setPoForm(prev => ({ ...prev, [field]: value }));
  };

  const onPoLineChange = (index, field, value) => {
    setPoForm(prev => {
      const lines = [...prev.lines];
      lines[index] = { ...lines[index], [field]: value };
      return { ...prev, lines };
    });
  };

  const addPoLine = () => {
    setPoForm(prev => ({ ...prev, lines: [...prev.lines, buildPoLine()] }));
  };

  const removePoLine = (index) => {
    setPoForm(prev => {
      if (prev.lines.length === 1) return prev;
      const lines = prev.lines.filter((_, i) => i !== index);
      return { ...prev, lines };
    });
  };

  const submitPo = () => {
    if (!poForm.supplierId || !poForm.locationId) {
      toast.error('Supplier and location are required');
      return;
    }

    const lines = poForm.lines
      .filter(line => line.inventoryItemId && Number(line.orderedQty) > 0)
      .map(line => {
        return {
          inventoryItemId: Number(line.inventoryItemId),
          catalogProductId: line.catalogProductId || null,
          modelNumber: line.inventoryItem?.modelNumber || null,
          orderedQty: Number(line.orderedQty),
          unitCost: line.unitCost === '' ? null : Number(line.unitCost),
          landingCost: line.landingCost === '' ? 0 : Number(line.landingCost),
          notes: line.notes || null
        };
      });

    if (!lines.length) {
      toast.error('Add at least one valid line');
      return;
    }

    createPoMutation.mutate({
      supplierId: Number(poForm.supplierId),
      locationId: Number(poForm.locationId),
      expectedDate: poForm.expectedDate || null,
      notes: poForm.notes || null,
      lines
    });
  };

  const onReceivePoSelect = (purchaseOrderId) => {
    const po = purchaseOrderById.get(Number(purchaseOrderId));

    if (!po) {
      setReceiveForm(prev => ({ ...prev, purchaseOrderId, lines: [] }));
      return;
    }

    const lines = (po.lines || []).map(line => {
      const remaining = Math.max(Number(line.orderedQty || 0) - Number(line.receivedQty || 0), 0);
      return {
        purchaseOrderLineId: line.id,
        modelNumber: line.modelNumber,
        inventoryItemId: line.inventoryItemId,
        inventoryItem: line.inventoryItem || null,
        catalogProductId: line.catalogProductId,
        receivedQty: remaining,
        acceptedQty: remaining,
        damagedQty: 0,
        unitCostAtReceipt: line.unitCost ?? '',
        landingCostAtReceipt: line.landingCost ?? 0,
        notes: ''
      };
    }).filter(line => Number(line.receivedQty) > 0);

    setReceiveForm(prev => ({
      ...prev,
      purchaseOrderId,
      lines
    }));
  };

  const onReceiveFieldChange = (field, value) => {
    setReceiveForm(prev => ({ ...prev, [field]: value }));
  };

  const addDirectReceiveLine = () => {
    setReceiveForm(prev => ({ ...prev, lines: [...prev.lines, buildDirectReceiveLine()] }));
  };

  const removeDirectReceiveLine = (index) => {
    setReceiveForm(prev => {
      if (prev.lines.length === 1) return prev;
      return { ...prev, lines: prev.lines.filter((_, i) => i !== index) };
    });
  };
  const onReceiveLineChange = (index, field, value) => {
    setReceiveForm(prev => {
      const lines = [...prev.lines];
      lines[index] = { ...lines[index], [field]: value };
      return { ...prev, lines };
    });
  };

  const submitReceive = () => {
    if (!receiveForm.supplierInvoiceNumber) {
      toast.error('Supplier invoice number is required');
      return;
    }

    if (receiveMode === 'po' && !receiveForm.purchaseOrderId) {
      toast.error('Select a purchase order');
      return;
    }

    if (receiveMode === 'direct' && (!receiveForm.supplierId || !receiveForm.locationId)) {
      toast.error('Supplier and location are required for direct receipt');
      return;
    }

    const lines = receiveForm.lines
      .filter(line => Number(line.receivedQty) > 0)
      .map(line => ({
        purchaseOrderLineId: line.purchaseOrderLineId,
        inventoryItemId: line.inventoryItemId,
        catalogProductId: line.catalogProductId || null,
        receivedQty: Number(line.receivedQty),
        acceptedQty: Number(line.acceptedQty || line.receivedQty),
        damagedQty: Number(line.damagedQty || 0),
        unitCostAtReceipt: line.unitCostAtReceipt === '' ? null : Number(line.unitCostAtReceipt),
        landingCostAtReceipt: line.landingCostAtReceipt === '' ? 0 : Number(line.landingCostAtReceipt),
        notes: line.notes || null
      }));

    if (!lines.length) {
      toast.error('Enter at least one received quantity');
      return;
    }

    const payload = {
      supplierInvoiceNumber: receiveForm.supplierInvoiceNumber,
      supplierInvoiceDate: receiveForm.supplierInvoiceDate || null,
      consignmentReference: receiveForm.consignmentReference || null,
      consignmentType: receiveForm.consignmentType || 'none',
      notes: receiveForm.notes || null,
      lines
    };

    if (receiveMode === 'po') {
      receivePoMutation.mutate({
        purchaseOrderId: Number(receiveForm.purchaseOrderId),
        payload
      });
      return;
    }

    directReceiveMutation.mutate({
      ...payload,
      supplierId: Number(receiveForm.supplierId),
      locationId: Number(receiveForm.locationId)
    });
  };

  const openPoOptions = purchaseOrders.filter(po => ['submitted', 'partially_received', 'draft'].includes(po.status));
  const selectedPurchaseOrder = receiveForm.purchaseOrderId ? purchaseOrderById.get(Number(receiveForm.purchaseOrderId)) || null : null;

  const cancelSelectedPurchaseOrder = (reason) => {
    if (!receiveForm.purchaseOrderId) {
      toast.error('Select a purchase order to cancel');
      return;
    }

    cancelPoMutation.mutate({
      purchaseOrderId: Number(receiveForm.purchaseOrderId),
      payload: { reason: reason || null }
    });
  };

  const onReceiptFilterChange = (field, value) => {
    setReceiptFilters(prev => ({ ...prev, [field]: value }));
    setReceiptPagination(prev => ({ ...prev, page: 0 }));
  };

  const clearReceiptFilters = () => {
    setReceiptFilters({
      invoiceNumber: '',
      supplierId: '',
      locationId: selectedLocation?.id || '',
      fromDate: '',
      toDate: ''
    });
    setReceiptPagination(prev => ({ ...prev, page: 0 }));
  };

  const onReceiptPageChange = (_, page) => {
    setReceiptPagination(prev => ({ ...prev, page }));
  };

  const onReceiptRowsPerPageChange = (event) => {
    const rowsPerPage = Number(event.target.value || 25);
    setReceiptPagination({ page: 0, rowsPerPage });
  };

  const openReceiptDetail = (receipt) => {
    setSelectedReceipt(receipt);
    setReceiptDetailOpen(true);
  };

  const placeQueuedOrder = (pendingOrderId, payload, onSuccess) => {
    if (!payload?.supplierId) {
      toast.error('Select a supplier before placing the order');
      return;
    }

    placeQueuedOrderMutation.mutate(
      { pendingOrderId, payload },
      {
        onSuccess: () => {
          onSuccess?.();
        }
      }
    );
  };

  const updateQueuedOrder = (pendingOrderId, payload, onSuccess) => {
    if (!String(payload?.modelNumber || '').trim()) {
      toast.error('Model number is required');
      return;
    }

    updateQueuedOrderMutation.mutate(
      { pendingOrderId, payload },
      {
        onSuccess: () => {
          onSuccess?.();
        }
      }
    );
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Procurement</Typography>
        <Typography variant="body2" color="text.secondary">
          Place supplier purchase orders and receive stock with supplier invoice and consignment details.
        </Typography>
        {selectedLocation?.name && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Using global location context: {selectedLocation.name}
          </Alert>
        )}
      </Paper>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="New Orders" data-testid="procurement-tab-new-orders" />
        <Tab label="Place Order" data-testid="procurement-tab-place-order" />
        <Tab label="Receive Stock" data-testid="procurement-tab-receive-stock" />
        <Tab label="Receipt History" data-testid="procurement-tab-receipt-history" />
      </Tabs>

      {tab === 0 && (
        <NewOrdersTab
          newOrders={newOrders}
          suppliers={suppliers}
          isLoading={isNewOrdersLoading}
          isSubmitting={placeQueuedOrderMutation.isLoading || updateQueuedOrderMutation.isLoading}
          searchText={newOrdersSearch}
          onSearchTextChange={setNewOrdersSearch}
          updateQueuedOrder={updateQueuedOrder}
          placeQueuedOrder={placeQueuedOrder}
        />
      )}

      {tab === 1 && (
        <PlaceOrderTab
          suppliers={suppliers}
          poForm={poForm}
          selectedLocationName={selectedLocation?.name || ''}
          onPoFieldChange={onPoFieldChange}
          onPoLineChange={onPoLineChange}
          addPoLine={addPoLine}
          removePoLine={removePoLine}
          setPoForm={setPoForm}
          submitPo={submitPo}
          isSubmitting={createPoMutation.isLoading}
        />
      )}

      {tab === 2 && (
        <ReceiveStockTab
          receiveMode={receiveMode}
          setReceiveMode={setReceiveMode}
          suppliers={suppliers}
          receiveForm={receiveForm}
          openPoOptions={openPoOptions}
          selectedPurchaseOrder={selectedPurchaseOrder}
          selectedLocationName={selectedLocation?.name || ''}
          onReceiveFieldChange={onReceiveFieldChange}
          onReceivePoSelect={onReceivePoSelect}
          onReceiveLineChange={onReceiveLineChange}
          addDirectReceiveLine={addDirectReceiveLine}
          removeDirectReceiveLine={removeDirectReceiveLine}
          setReceiveForm={setReceiveForm}
          buildDirectReceiveLine={buildDirectReceiveLine}
          submitReceive={submitReceive}
          onCancelPurchaseOrder={cancelSelectedPurchaseOrder}
          isSubmitting={receivePoMutation.isLoading || directReceiveMutation.isLoading || cancelPoMutation.isLoading}
        />
      )}

      {tab === 3 && (
        <ReceiptHistoryTab
          suppliers={suppliers}
          receiptFilters={receiptFilters}
          onReceiptFilterChange={onReceiptFilterChange}
          clearReceiptFilters={clearReceiptFilters}
          isReceiptsLoading={isReceiptsLoading}
          receipts={receipts}
          receiptCount={receiptCount}
          receiptPagination={receiptPagination}
          onReceiptPageChange={onReceiptPageChange}
          onReceiptRowsPerPageChange={onReceiptRowsPerPageChange}
          isReceiptsFetching={isReceiptsFetching}
          openReceiptDetail={openReceiptDetail}
          receiptDetailOpen={receiptDetailOpen}
          setReceiptDetailOpen={setReceiptDetailOpen}
          selectedReceipt={selectedReceipt}
        />
      )}

    </Box>
  );
}
